import { useQuery } from '@tanstack/react-query'
import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'
import { mockState } from '@/api/mock-data'
import type { OrderStatus } from '@/types/enums'
import { MAP_ACTIVE_STATUSES } from '@/features/map/orderMapStatusColors'
import { useRoleOrderParams } from '@/hooks/useRoleOrderParams'

export interface OrderMapRow {
  id: string
  status: OrderStatus
  brand: string
  model: string
  desired_price: number | null
  final_price: number | null
  address: string
  lat: number
  lon: number
  inspector_id: string | null
  manager_id: string | null
}

function isActiveStatus(s: string): s is OrderStatus {
  return MAP_ACTIVE_STATUSES.includes(s as OrderStatus)
}

async function fetchMarkers(): Promise<OrderMapRow[]> {
  if (USE_MOCK) {
    const rows = mockState.orders.filter(
      (o) =>
        isActiveStatus(o.status) &&
        o.lat != null &&
        o.lon != null &&
        !Number.isNaN(Number(o.lat)) &&
        !Number.isNaN(Number(o.lon)),
    )
    return rows.map((o) => ({
      id: o.id,
      status: o.status as OrderStatus,
      brand: o.brand,
      model: o.model,
      desired_price: o.desired_price,
      final_price: o.final_price ?? null,
      address: o.address,
      lat: Number(o.lat),
      lon: Number(o.lon),
      inspector_id: o.inspector_id,
      manager_id: o.manager_id,
    }))
  }

  const supabase = createClient()
  const { data, error } = await supabase
    .from('orders')
    .select(
      'id, status, brand, model, desired_price, final_price, address, lat, lon, inspector_id, manager_id',
    )
    .in('status', MAP_ACTIVE_STATUSES as unknown as string[])
    .not('lat', 'is', null)
    .not('lon', 'is', null)

  if (error) throw new Error(error.message)
  const list = (data ?? []) as Array<Record<string, unknown>>
  return list
    .map((r) => ({
      id: String(r.id),
      status: r.status as OrderStatus,
      brand: String(r.brand ?? ''),
      model: String(r.model ?? ''),
      desired_price:
        r.desired_price === null || r.desired_price === undefined
          ? null
          : Number(r.desired_price),
      final_price:
        r.final_price === null || r.final_price === undefined
          ? null
          : Number(r.final_price),
      address: String(r.address ?? ''),
      lat: Number(r.lat),
      lon: Number(r.lon),
      inspector_id: (r.inspector_id as string | null) ?? null,
      manager_id: String(r.manager_id ?? ''),
    }))
    .filter((r) => !Number.isNaN(r.lat) && !Number.isNaN(r.lon))
}

async function fetchActiveTotal(): Promise<number> {
  if (USE_MOCK) {
    return mockState.orders.filter((o) => isActiveStatus(o.status)).length
  }
  const supabase = createClient()
  const { count, error } = await supabase
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .in('status', MAP_ACTIVE_STATUSES as unknown as string[])

  if (error) throw new Error(error.message)
  return count ?? 0
}

export function useOrdersForMap(options?: { enabled?: boolean }) {
  const roleParams = useRoleOrderParams()
  const enabled = options?.enabled ?? true

  const markersQuery = useQuery({
    queryKey: ['orders', 'map', roleParams],
    queryFn: fetchMarkers,
    enabled,
  })

  const activeTotalQuery = useQuery({
    queryKey: ['orders', 'map', 'active-total', roleParams],
    queryFn: fetchActiveTotal,
    enabled,
  })

  const n = markersQuery.data?.length ?? 0
  const m = activeTotalQuery.data ?? 0

  return {
    markers: markersQuery.data ?? [],
    n,
    m,
    isLoading: markersQuery.isLoading || activeTotalQuery.isLoading,
    isError: markersQuery.isError || activeTotalQuery.isError,
    error: markersQuery.error ?? activeTotalQuery.error,
  }
}
